import groq from "groq"

import { groqQueryOptions } from "@/graphql/schemas/shared/utils/groqQueryOptions"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { checkPermissions } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import { groqBranchOfficeProjection } from "../../shared/sanity/branchOffice.projection"
import { getStockKeys } from "../../shared/utils/getVariantListing"

export const queryProductStock = {
  async productStock(parent, args, context, info) {
    const { sku, branchId, cdn, lang } = args
    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.PRODUCT_STOCK,
      operation: Operations.READ,
    })

    return getProductStockByBranchId({ context, sku, branchId, cdn, lang })
  },
}

export const getProductStockByBranchId = async ({ context, sku, branchId, cdn, lang }) => {
  let branchIdFilter = typeof branchId === "string" || typeof branchId === "number" ? "&& id==$branchId " : ""

  const config = AppSettings.sanityConfigByLang(lang)
  const client = context.sanityClient
  client.config({ ...config, useCdn: cdn })

  const commerceApi = CommerceService.api()

  const { draftsFilter } = groqQueryOptions({ includeDrafts: true })

  const branchOffice = await client.fetch(
    groq`*[_type=="branchOffice" ${branchIdFilter}${draftsFilter}][0] {
        ...,
        ${groqBranchOfficeProjection()}
      }`,
    { branchId },
  )

  if (!branchOffice) return null

  const stockKeysWithSkus = await getStockKeys({ skus: [sku] })

  const stockKeys = stockKeysWithSkus.map((stockKey) => `"${stockKey?.stockKey}"`).join(", ")

  const listings = (
    await commerceApi
      .inventory()
      .get({
        queryArgs: {
          limit: 500,
          expand: "supplyChannel",
          where: `sku=${stockKeys}`,
        },
      })
      .execute()
  )?.body.results

  const stocksInBranch = () => {
    if (!branchOffice) {
      return {
        branchId: null,
        branchName: null,
        availableStocks: null,
        quantityOnStock: null,
        sku,
      }
    }

    let branchData = {
      branchId: branchOffice.id,
      branchName: branchOffice.title,
      availableStocks: null,
      quantityOnStock: null,
      reservedStock: null,
      sku,
    }

    if (!listings || listings.length <= 0) {
      return branchData
    }

    const branchListing = listings.find((listing) => listing.supplyChannel.obj.key === `NL-${branchOffice.id}`)

    const customStockFields = branchListing?.custom?.fields

    if (!customStockFields) return branchData

    if (typeof customStockFields?.adm_availableQuantity === "number") {
      branchData.availableStocks = customStockFields?.adm_availableQuantity
    }

    if (typeof customStockFields?.adm_quantityOnStock === "number") {
      branchData.quantityOnStock = customStockFields?.adm_quantityOnStock
    }

    if (typeof customStockFields?.adm_reservedQuantity === "number") {
      branchData.reservedStock = customStockFields?.adm_reservedQuantity
    }

    return branchData
  }

  return stocksInBranch()
}
